import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, FileText, DollarSign, Star, Search, ShieldCheck, Zap, Phone, Plus, X } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PushToggle from "@/components/notifications/PushToggle";
import AccountChecklist from "@/components/profile/AccountChecklist";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PHONE_NUDGE_KEY = "fixydany_phone_nudge_closed";

export default function ProviderDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const profile = user?.profile || {};
  const name = profile.full_name?.split(" ")[0] || "—";
  const tier = profile.tier_priority || 0;
  const tierLabel = ["Starter", "Pro", "Master"][tier];

  // Live KPI counts (quotes sent, active jobs, rating, completed) — the cached
  // AuthContext profile can be stale, so fetch fresh stats on mount.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/users/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Verification as trust (PLANES M6b): the card says where they stand — not
  // started, in review, approved — so a FixyBee who already sent documents is
  // not told to "start" again. `pending_request` is an open manual review or a
  // Stripe Identity session still processing.
  const [verification, setVerification] = useState(null);
  useEffect(() => {
    if (profile.is_verified) return;
    api.get("/verification/status").then((r) => setVerification(r.data)).catch(() => {});
  }, [profile.is_verified]);
  const verifyState = profile.is_verified
    ? "approved"
    : verification?.pending_request ? "review" : "none";

  // The phone nudge (2026-10-28): a quiet strip while the phone is not
  // confirmed, closable for the rest of the session. A suggestion — clients
  // trust a confirmed phone — never "required": nothing waits on it.
  const [nudgeClosed, setNudgeClosed] = useState(() => {
    try { return sessionStorage.getItem(PHONE_NUDGE_KEY) === "1"; } catch { return false; }
  });
  const closeNudge = () => {
    setNudgeClosed(true);
    try { sessionStorage.setItem(PHONE_NUDGE_KEY, "1"); } catch { /* private mode */ }
  };
  const showNudge = profile.phone_verified === false && !nudgeClosed;

  const kpis = [
    { key: "quotes-sent", label: t("dashboard.provider.kpiQuotesSent"), value: stats?.quotesSent ?? 0, icon: FileText },
    { key: "active", label: t("dashboard.provider.kpiActive"), value: stats?.active ?? 0, icon: Briefcase },
    { key: "rating", label: t("dashboard.provider.kpiRating"), value: (stats?.rating ?? profile.average_rating) || "—", icon: Star },
    { key: "completed", label: t("dashboard.provider.kpiCompleted"), value: (stats?.completed ?? profile.completed_jobs_count) || 0, icon: DollarSign },
  ];

  return (
    <DashboardLayout role="provider">
      <div data-testid="provider-dashboard-content">
        {showNudge && (
          <div className="mb-6 flex items-center gap-3 rounded-sm border border-sand bg-linen pl-4 pr-1 py-1 text-sm text-worker"
               role="status" data-testid="phone-nudge">
            <Phone size={16} className="shrink-0 text-smoke" aria-hidden="true" />
            <p className="min-w-0 flex-1">
              {t("signupX.phoneNudge")}{" "}
              <Link to="/provider/verify-phone" className="font-semibold underline" data-testid="phone-nudge-cta">
                {t("signupX.itemPhoneCta")}
              </Link>
            </p>
            <button type="button" onClick={closeNudge} aria-label={t("signupX.nudgeClose")}
                    className="h-11 w-11 shrink-0 inline-flex items-center justify-center text-smoke hover:text-worker"
                    data-testid="phone-nudge-close">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
              {t("dashboard.provider.welcome", { name })}
            </h1>
            <p className="mt-1 text-smoke flex items-center gap-2">
              <span className={`badge ${tier === 2 ? "badge-featured" : tier === 1 ? "badge-verified" : ""}`}>
                {tierLabel}
              </span>
              {profile.is_verified && (
                <span className="badge badge-verified" data-testid="verified-badge">
                  <ShieldCheck size={12} /> {t("dashboard.provider.verified")}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/provider/browse" className="fx-btn fx-btn--primary" data-testid="browse-jobs-cta">
              <Search size={18} /> {t("dashboard.provider.ctaBrowse")}
            </Link>
            {/* T25: posting is half of what this account can do (DY2.6), and
                at 375px the sidebar's "My requests" sits behind the menu. */}
            <Link to="/provider/requests/new" className="fx-btn fx-btn--secondary" data-testid="provider-post-job-cta">
              <Plus size={18} /> {t("dashboard.provider.ctaPostJob")}
            </Link>
          </div>
        </div>

        <AccountChecklist profile={profile} verifyState={verifyState} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.key} className="card-base p-4" data-testid={`kpi-${k.key}`}>
                <Icon size={18} className="text-smoke" />
                <p className="mt-3 font-mono text-3xl font-bold text-worker">{k.value}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-smoke font-display">{k.label}</p>
              </div>
            );
          })}
        </div>

        {/* On the dashboard, not buried in Settings (Jorge, 2026-08-20). It
            matters more here than on the client side: a FixyBee who misses the
            FixyMatch push misses the job. */}
        <div className="mb-10">
          <PushToggle />
        </div>

        {/* DY2.6. Only when they actually have one: a FixyBee who has never
            needed to hire anybody should not be shown an empty half of a
            product. The sidebar entry is always there for the ones who want to
            start. `requestsOpen` is a count of THEIR OWN open jobs — never the
            board, which is `/provider/browse`. */}
        {stats?.requestsOpen > 0 && (
          <div className="card-base p-5 mb-10 flex items-center justify-between flex-wrap gap-3"
               data-testid="own-requests-card">
            <div className="min-w-0">
              <h3 className="font-display text-xl font-bold text-worker">
                {t("dashboard.provider.requestsTitle", { count: stats.requestsOpen })}
              </h3>
              <p className="mt-1 text-smoke text-sm">
                {t("dashboard.provider.requestsBody", { count: stats.requestsQuotes ?? 0 })}
              </p>
            </div>
            <Link to="/provider/requests" className="fx-btn fx-btn--secondary" data-testid="own-requests-cta">
              <Briefcase size={18} /> {t("dashboard.provider.requestsCta")}
            </Link>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {/* Starter and Pro see the way to more quotes a month. Master has no
              card here: its variant was entirely about FixyTerminal, which is
              out of scope and on no surface (PLANES, 2026-09-30). */}
          {tier < 2 && (
          <div className="card-base p-6" data-testid="upgrade-card">
            <Zap size={22} className="text-hive-ink" />
            <h3 className="mt-3 font-display text-2xl font-bold text-worker">
              {t(`dashboard.provider.upgradeTitle${tier}`)}
            </h3>
            <p className="mt-2 text-smoke">
              {t(`dashboard.provider.upgradeBody${tier}`)}
            </p>
            <Link to="/provider/billing" className="fx-btn fx-btn--secondary mt-6" data-testid="upgrade-cta">
              {t(`dashboard.provider.upgradeCta${tier}`)}
            </Link>
          </div>
          )}

          <div className="card-base p-6" data-testid="verify-card" data-state={verifyState}>
            <div className="flex items-center justify-between gap-3">
              <ShieldCheck size={22} className="text-hive-ink" />
              <span className={`badge text-[10px] ${verifyState === "approved" ? "badge-verified" : ""}`}
                    data-testid="verify-state">
                {verifyState === "approved"
                  ? t("dashboard.provider.verified")
                  : verifyState === "review"
                    ? t("verificationX.pendingInReview")
                    : t("dashboard.provider.verifyStateNone")}
              </span>
            </div>
            <h3 className="mt-3 font-display text-2xl font-bold text-worker">
              {verifyState === "approved" ? t("dashboard.provider.verifiedTitle") : t("dashboard.provider.verifyTitle")}
            </h3>
            <p className="mt-2 text-smoke">
              {verifyState === "approved" ? t("dashboard.provider.verifiedBody") : t("dashboard.provider.verifyBody")}
            </p>
            {verifyState !== "approved" && (
              <Link to="/provider/verification" className="fx-btn fx-btn--secondary mt-6 inline-flex" data-testid="verify-cta">
                {verifyState === "review" ? t("dashboard.provider.verifyCtaReview") : t("dashboard.provider.verifyCta")}
              </Link>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
